import { IFormStep } from '../../utils/formStep';
import React, { MouseEvent, useState } from 'react';
import { withTranslation } from 'react-i18next';
import Notification, { INotificationErrorMessage } from '../../../notification/notification';
import OrganizationStep3Provider from './organizationStep3Provider';
import fetchFormData, { IFormDataModel } from '../../../../helpers/fetchFormData';
import {
    IProviderModel,
    ProviderModel,
    providerModelFromFormData,
} from '../../../../helpers/models/providerModel';
import { updateProviders } from '../../../../helpers/providers';
import { updateAccount } from '../../../../helpers/account';
import { ELanguage } from '../../utils/language';
import {
    checkEmailIsDifferentFromClinicEmail,
    handleRequiredError,
    scrollToFormErrors,
    validateEmail,
    validatePastDate,
    validatePositiveInt,
} from '../../utils/formErrors';
import { IRoleModel } from '../../../../helpers/models/roleModel';
import { IProvinceModel } from '../../../../helpers/models/provinceModel';
import {
    IOrganizationStep3FormResultKeys,
    OrganizationStep3FormResultKeys,
} from './organizationStep3FormModel';
import { startOverId } from '../../registration';
import { listenToAccessibilityKeyboardInputs } from '../../../../accessibleNavigation';

const OrganizationStep3 = (props: IFormStep) => {
    const { t, account, i18n } = props;
    const [providers, setProviders] = useState<ProviderModel[]>(
        account.provider.length > 0 ? (account.provider as ProviderModel[]) : [new ProviderModel()]
    );
    const [editedProviders, setEditedProviders] = useState<ProviderModel[] | undefined>();
    const handleOnAddClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.currentTarget.blur();
        const newProviders = [...providers];
        newProviders.push(new ProviderModel());
        setProviders(newProviders);

        // Wait for update then adjust focus element
        setTimeout(() => {
            document
                .getElementById(
                    OrganizationStep3FormResultKeys.FIRST_NAME + `_${newProviders.length - 1}`
                )
                ?.focus();
            listenToAccessibilityKeyboardInputs(
                document.getElementById(
                    `form_organization_step_3_delete_${newProviders.length - 1}`
                )
            );
        }, 50);
    };

    const handleOnDeleteClick = (e: MouseEvent<HTMLImageElement>) => {
        e.preventDefault();

        const form = document.getElementById('organization-step-3-form')! as HTMLFormElement;
        const data = fetchFormData(form);
        const dividedData = fetchProvidersFromData(data);

        const providersArray: IProviderModel[] = [];

        dividedData.forEach((item, i) => {
            item = item.map((x) => {
                if (x.checked) {
                    return {
                        name: x.name.slice(0, x.name.length - 2),
                        value: x.value,
                        checked: x.checked,
                    };
                }

                return { name: x.name.slice(0, x.name.length - 2), value: x.value };
            });

            const formResult = Object.assign(
                {},
                ...item.map((x) => ({ [x.name]: x.value }))
            ) as IOrganizationStep3FormResultKeys;
            const provider = generateProviderEntity(item, data, formResult, i);

            providersArray.push({ ...(provider as IProviderModel) });
        });

        const newProviders = [...providersArray];

        newProviders.splice(+e.currentTarget.dataset.id!, 1);
        form.reset();

        setEditedProviders(newProviders as ProviderModel[]);
        setProviders(newProviders as ProviderModel[]);
        document.getElementById('button_add_item')!.focus();
    };

    const providersComponent = providers.map((item, index) => {
        return (
            <OrganizationStep3Provider
                key={`organization_step_3_form_${index}`}
                index={index}
                {...props}
                handleOnDeleteClick={handleOnDeleteClick}
                providersOverwrite={editedProviders}
            />
        );
    });

    const fetchProvidersFromData = (data: IFormDataModel[]) => {
        const dividedData: (
            | { name: any; value: any; checked: any }
            | { name: any; value: any; checked?: undefined }
        )[][] = [];

        providers.forEach((x, i) => {
            const filtered = data.filter((item) => item.name.endsWith(i.toString()));
            dividedData.push(filtered);
        });

        return dividedData;
    };

    const generateProviderEntity = (
        item: any,
        data: IFormDataModel[],
        formResult: IOrganizationStep3FormResultKeys,
        index: number
    ) => {
        const provider = providerModelFromFormData(formResult);

        provider.lng =
            data.find(
                (item) =>
                    item.name === OrganizationStep3FormResultKeys.LNG + `_${index}` && item.checked
            )?.value === ELanguage.English.toString()
                ? 'en'
                : 'fr';
        provider.role = Number(
            data.find(
                (item) => item.name === OrganizationStep3FormResultKeys.PROFESSION + `_${index}`
            )?.value
        );
        provider.issuer = Number(
            data.find((item) => item.name === OrganizationStep3FormResultKeys.ISSUER + `_${index}`)
                ?.value
        );
        provider.comment = data.find(
            (item) => item.name === OrganizationStep3FormResultKeys.COMMENT + `_${index}`
        )?.value!;
        provider.licenceNumber = data.find(
            (item) => item.name === OrganizationStep3FormResultKeys.NUMBER + `_${index}`
        )?.value!;
        provider.licenceDate = data.find(
            (item) => item.name === OrganizationStep3FormResultKeys.ISSUE_DATE + `_${index}`
        )?.value!;
        provider.email = data.find(
            (item) => item.name === OrganizationStep3FormResultKeys.EMAIL + `_${index}`
        )?.value!;
        provider.firstname = data.find(
            (item) => item.name === OrganizationStep3FormResultKeys.FIRST_NAME + `_${index}`
        )?.value!;
        provider.lastname = data.find(
            (item) => item.name === OrganizationStep3FormResultKeys.LAST_NAME + `_${index}`
        )?.value!;

        return provider;
    };

    const handleSubmitOrganizationStep3 = (e: any) => {
        e.preventDefault();
        const data = fetchFormData(e);
        const errorList: INotificationErrorMessage[] = [];
        const dividedData = fetchProvidersFromData(data);

        const providersArray: IProviderModel[] = [];

        dividedData.forEach((item, i) => {
            item = item.map((x: any) => {
                if (x.checked) {
                    return {
                        name: x.name.slice(0, x.name.length - 2),
                        value: x.value,
                        checked: x.checked,
                    };
                }

                return { name: x.name.slice(0, x.name.length - 2), value: x.value };
            });

            const formResult = Object.assign(
                {},
                ...item.map((x: any) => ({ [x.name]: x.value }))
            ) as IOrganizationStep3FormResultKeys;

            const provider = generateProviderEntity(item, data, formResult, i);
            providersArray.push({ ...provider });

            // Validate required fields
            const itemErrors = handleRequiredError(formResult, data, t, i);

            if (formResult.form_organization_step_3_field_number) {
                const numberValidation = validatePositiveInt(
                    `${OrganizationStep3FormResultKeys.NUMBER}_${i}`,
                    +formResult.form_organization_step_3_field_number,
                    t
                );
                if (numberValidation) {
                    errorList.push({
                        messagePrefix: 'form_generic_positive_int_error_prefix',
                        message: 'form_organization_step_3_number',
                        messageSuffix: 'form_generic_positive_int_error_suffix',
                        messageEOL: 'form_multiple_error_suffix',
                        messageEOLId: (i + 1).toString(),
                        lowerCaseMessage: true,
                        focusElement: `${OrganizationStep3FormResultKeys.NUMBER}_${i}`,
                        weight: 4 + i * 6,
                    });
                }
            }

            if (formResult.form_organization_step_3_field_email) {
                const emailValidation = validateEmail(
                    `${OrganizationStep3FormResultKeys.EMAIL}_${i}`,
                    formResult.form_organization_step_3_field_email,
                    t
                );
                if (emailValidation) {
                    errorList.push({
                        message: emailValidation,
                        focusElement: `${OrganizationStep3FormResultKeys.EMAIL}_${i}`,
                        weight: 2 + i * 7,
                    });
                } else {
                    const targetEmail = account.organization.email;
                    const sameEmail = checkEmailIsDifferentFromClinicEmail(
                        `${OrganizationStep3FormResultKeys.EMAIL}_${i}`,
                        formResult.form_organization_step_3_field_email,
                        targetEmail,
                        t
                    );
                    if (sameEmail) {
                        errorList.push({
                            message: sameEmail,
                            focusElement: `${OrganizationStep3FormResultKeys.EMAIL}_${i}`,
                            weight: 2 + i * 7,
                        });
                    }
                }
            }

            if (formResult.form_organization_step_3_field_issue_date) {
                const dateValidation = validatePastDate(
                    `${OrganizationStep3FormResultKeys.ISSUE_DATE}_${i}`,
                    formResult.form_organization_step_3_field_issue_date,
                    t
                );
                if (dateValidation) {
                    errorList.push({
                        messageSuffix: 'form_multiple_error_suffix',
                        message: dateValidation,
                        focusElement: `${OrganizationStep3FormResultKeys.ISSUE_DATE}_${i}`,
                        weight: 5 + i * 6,
                    });
                }
            }

            // Append suffix on each error items
            itemErrors.forEach((error: INotificationErrorMessage) => {
                errorList.unshift({
                    message: 'form_multiple_error_suffix',
                    messagePrefix: error.message,
                    messageSuffix: (i + 1).toString(),
                    focusElement: error.focusElement,
                    weight: error.weight,
                });
            });
        });

        if (!errorList.length) {
            // set Providers
            updateProviders(i18n.language, account.id, providersArray);

            // update step in account
            const newFormData = Object.assign({}, account);
            if (newFormData.step >= 1) {
                newFormData.step++;
            }

            // set number to province and role instead of the returned object
            newFormData.province = +(account.province as IProvinceModel).id;
            newFormData.role = +(account.role as IRoleModel).id;

            updateAccount(i18n.language, account.id, { ...newFormData });
        }

        if (errorList.length) {
            props.setError(errorList);
            scrollToFormErrors();
        }
    };

    return (
        <div className="registration-organization_step_container">
            <Notification
                error={false}
                label={t('popover_aria_label', { ns: 'accessibility' })}
                messages={[{ message: 'form_organization_step_3_notice', weight: 0 }]}
            />
            <form
                className="registration-container"
                id="organization-step-3-form"
                onSubmit={handleSubmitOrganizationStep3}
            >
                <div className="header-3 header-3--purple">
                    {t('form_organization_step_3_title')}
                </div>
                {providersComponent}
                <button
                    onClick={handleOnAddClick}
                    id="button_add_item"
                    className="button button--secondary registration-organization-button"
                >
                    {t('form_organization_step_3_button_add')}
                </button>
                <div className="registration-organization-button-container">
                    <button
                        className="button button--secondary registration-organization-button"
                        onClick={props.startOver}
                        id={startOverId}
                    >
                        {t('form_organization_step_3_button_restart')}
                    </button>
                    <div className="registration-organization-standalone-button-container">
                        <a
                            tabIndex={0}
                            data-step={2}
                            className="standalone-link registration-button-standalone-link registration-organization-standalone-link"
                            onClick={props.goBack}
                            role="button"
                        >
                            {t('form_organization_step_3_button_previous')}
                        </a>
                        <button
                            className="button button--primary registration-organization-button"
                            type="submit"
                        >
                            {t('form_organization_step_3_button_next')}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default withTranslation()(OrganizationStep3);
